import { useState, useMemo } from "react";
import AccountLayout from "@/components/feature/AccountLayout";
import { useOrders } from "@/hooks/useOrders";
import { statusLabelsEn, statusLabelsAr } from "@/mocks/orders";
import type { Order } from "@/mocks/orders";

type StatusFilter = "all" | Order["status"];

export default function OrdersPage() {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language?.startsWith("ar");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const { orders, loading } = useOrders();

  const statusStyles: Record<string, string> = {
    delivered: "bg-secondary-100 text-secondary-700",
    shipped: "bg-accent-100 text-accent-700",
    processing: "bg-primary-100 text-primary-700",
    cancelled: "bg-foreground-100 text-foreground-500",
  };

  const getStatusLabel = (status: string) =>
    isAr ? statusLabelsAr[status as keyof typeof statusLabelsAr] : statusLabelsEn[status as keyof typeof statusLabelsEn];

  const filteredOrders = useMemo(() => {
    if (statusFilter === "all") return orders;
    return orders.filter((o) => o.status === statusFilter);
  }, [statusFilter, orders]);

  const filters: { value: StatusFilter; label: string }[] = [
    { value: "all", label: isAr ? "الكل" : "All" },
    { value: "processing", label: getStatusLabel("processing") },
    { value: "shipped", label: getStatusLabel("shipped") },
    { value: "delivered", label: getStatusLabel("delivered") },
    { value: "cancelled", label: getStatusLabel("cancelled") },
  ];

  return (
    <AccountLayout activeTab="orders">
      {/* Status filter tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {filters.map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
              statusFilter === f.value
                ? "bg-primary-500 text-white"
                : "text-foreground-600 bg-background-100 hover:bg-background-200"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin"></div>
        </div>
      ) : filteredOrders.length > 0 ? (
        <div className="space-y-4">
          {filteredOrders.map((order) => (
            <Link
              key={order.id}
              to={`/account/orders/${order.id}`}
              className="block bg-background-50 border border-background-200 rounded-xl overflow-hidden hover:border-background-300 transition-colors cursor-pointer"
            >
              {/* Order header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-background-100">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-sm font-medium text-foreground-900">
                      {t("account.order_number")} #{order.orderNumber}
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {new Date(order.date).toLocaleDateString(
                        isAr ? "ar-EG" : "en-US",
                        { year: "numeric", month: "long", day: "numeric" }
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${statusStyles[order.status]}`}
                  >
                    {getStatusLabel(order.status)}
                  </span>
                  <span className="text-base font-semibold text-foreground-950">
                    ${order.total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Order items preview */}
              <div className="p-5 flex flex-wrap items-center gap-3">
                {order.items.map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-background-100 overflow-hidden flex-shrink-0">
                      <img
                        src={item.imageUrl}
                        alt={isAr ? item.nameAr : item.nameEn}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="text-sm text-foreground-800 line-clamp-1">
                        {isAr ? item.nameAr : item.nameEn}
                      </p>
                      <p className="text-xs text-foreground-500">
                        {t("cart.quantity")}: {item.quantity}
                      </p>
                    </div>
                    {i < order.items.length - 1 && (
                      <span className="text-foreground-300 mx-1">+</span>
                    )}
                  </div>
                ))}
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
            <i className="ri-inbox-line text-2xl text-foreground-400"></i>
          </div>
          <h3 className="text-lg font-medium text-foreground-900 mb-2">{t("account.no_orders")}</h3>
          <p className="text-sm text-foreground-500 mb-6">{t("account.no_orders_desc")}</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-semibold rounded-full hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap text-sm"
          >
            {t("account.start_shopping")}
          </Link>
        </div>
      )}
    </AccountLayout>
  );
}